import React from 'react';
import {
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { ChatTag, ChatTagCategory, FAMILY_LABELS } from '../tagGlossary';

export interface TagRow {
  tag: ChatTag;
  category: ChatTagCategory;
}

interface Props {
  rows: TagRow[];
  /** Search results span categories: the category is a column then, and nothing moves. */
  showCategory: boolean;
  canEdit: boolean;
  /** The tag whose Retire was clicked once: the next click retires it. */
  confirmRetireId: string | null;
  onEdit: (row: TagRow) => void;
  onRetire: (row: TagRow) => void;
  onRestore: (row: TagRow) => void;
  onMove: (row: TagRow, index: number, direction: 'up' | 'down') => void;
  onRetireBlur: () => void;
}

/** A category's tags (or a search's hits): label, id, applies-to, definition, notes, rider report id, status. */
export function TagTable({
  rows,
  showCategory,
  canEdit,
  confirmRetireId,
  onEdit,
  onRetire,
  onRestore,
  onMove,
  onRetireBlur,
}: Props) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No tags.
      </Typography>
    );
  }
  return (
    <Table size="small" aria-label="Tags">
      <TableHead>
        <TableRow>
          <TableCell>Label</TableCell>
          {showCategory && <TableCell>Category</TableCell>}
          <TableCell>Applies to</TableCell>
          <TableCell>Definition</TableCell>
          <TableCell>Notes</TableCell>
          <TableCell>Rider report id</TableCell>
          <TableCell>Status</TableCell>
          {canEdit && <TableCell align="right">Actions</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => {
          const retired = Boolean(row.tag.archivedAt);
          return (
            <TableRow key={row.tag.id} data-testid="tag-row" hover>
              <TableCell sx={{ minWidth: 200 }}>
                <Typography variant="body2">{row.tag.label}</Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {row.tag.slug}
                </Typography>
              </TableCell>
              {showCategory && <TableCell>{row.category.name}</TableCell>}
              <TableCell>
                {row.tag.appliesTo.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    All rentals
                  </Typography>
                ) : (
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {row.tag.appliesTo.map((f) => (
                      <Chip
                        key={f}
                        size="small"
                        variant="outlined"
                        label={FAMILY_LABELS[f]}
                      />
                    ))}
                  </Stack>
                )}
              </TableCell>
              <TableCell sx={{ maxWidth: 360 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {row.tag.definition ?? ''}
                </Typography>
              </TableCell>
              <TableCell sx={{ maxWidth: 240 }}>
                <Typography variant="body2" color="text.secondary">
                  {row.tag.notes ?? ''}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {row.tag.riderReportTagId ?? ''}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={retired ? 'Retired' : 'Active'}
                  color={retired ? 'default' : 'success'}
                  variant={retired ? 'outlined' : 'filled'}
                />
              </TableCell>
              {canEdit && (
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {!showCategory && (
                    <>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Move up ${row.tag.label}`}
                            disabled={index === 0}
                            onClick={() => onMove(row, index, 'up')}
                          >
                            <ArrowUpwardIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Move down ${row.tag.label}`}
                            disabled={index === rows.length - 1}
                            onClick={() => onMove(row, index, 'down')}
                          >
                            <ArrowDownwardIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                  <Button size="small" onClick={() => onEdit(row)}>
                    Edit
                  </Button>
                  {retired ? (
                    <Button size="small" onClick={() => onRestore(row)}>
                      Restore
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => onRetire(row)}
                      onBlur={onRetireBlur}
                    >
                      {confirmRetireId === row.tag.id ? 'Confirm retire' : 'Retire'}
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
